<div class="page-shell page-shell--canvas">

  <form [formGroup]="form" (ngSubmit)="save()" class="dc-wrapper">
    <div class="dc-header">
      <div class="dc-header__start">
        <mat-form-field appearance="outline" class="dc-name-field">
          <mat-label>{{ "digitalCards.displayName" | translate }}</mat-label>
          <input
            matInput
            formControlName="displayName"
            [placeholder]="'digitalCards.displayNamePlaceholder' | translate"
            [errorStateMatcher]="errorStateMatcher"
          />
          @if (displayName) {
            <mat-error>{{
              "digitalCards.displayNameRequired" | translate
            }}</mat-error>
          }
        </mat-form-field>
      </div>
      <div class="dc-header__actions">
        <button mat-stroked-button type="button" (click)="back()">
          {{ "cancel" | translate }}
        </button>
        <button
          mat-raised-button
          type="button"
          color="primary"
          (click)="save()"
          [disabled]="saving || loading || !pageReady"
        >
          @if (saving) {
            <mat-spinner diameter="20" class="dc-btn-spinner"></mat-spinner>
          } @else {
            <mat-icon class="dc-btn-icon">save</mat-icon>
          }
          {{ saving ? ("saving" | translate) : ("save" | translate) }}
        </button>
      </div>
    </div>
    <div class="dc-editor-grid">
      <div class="dc-section dc-preview-panel dc-preview-panel--editor">
        <div class="dc-section__content dc-preview-panel__body">
          <h3 class="dc-preview-panel__title">
            {{ "digitalCards.previewTitle" | translate }}
          </h3>
          <div
            class="dc-employee-picker-wrap dc-editor-preview-employee-picker"
          >
            <app-digital-card-employee-picker
              [value]="previewEmployee"
              [templateContextId]="activeTemplateId"
              (valueChange)="onPreviewEmployeeChange($event)"
            ></app-digital-card-employee-picker>
          </div>
          <div class="dc-preview-panel__stage">
            <app-card-digital-preview
              [previewUrl]="previewIframeUrl"
              [snapshot]="previewSnapshot"
              [bannerImageUrl]="bannerPreviewUrl ?? null"
              [previewSurface]="previewSurface"
              [showAppleWallet]="appleWalletEnabled"
              [showGoogleWallet]="googleWalletEnabled"
              [loading]="previewEmployeeLoading"
              [placeholderText]="'digitalCards.previewPlaceholder' | translate"
            ></app-card-digital-preview>
          </div>
        </div>
      </div>

      <div class="dc-section dc-form-panel">
        <div class="dc-section__content">
          <div class="dc-form">
            <mat-tab-group
              animationDuration="0ms"
              class="dc-editor-tabs"
              [selectedIndex]="editorTabIndex"
              (selectedIndexChange)="onEditorTabIndexChange($event)"
            >
              <mat-tab>
                <ng-template mat-tab-label>
                  <mat-icon class="dc-tab-icon">contact_page</mat-icon>
                  {{ "digitalCards.tabAppearance" | translate }}
                </ng-template>
                <div class="dc-tab-body">
                  @if (enabledProfileLanguages.length > 0) {
                    <section class="dc-block">
                      <header class="dc-block__head">
                        <h3 class="dc-block__title">
                          {{ "digitalCards.sectionProfileLanguage" | translate }}
                        </h3>
                      </header>
                      <div class="dc-block__body">
                        <mat-form-field
                          appearance="outline"
                          class="dc-form-field-full"
                        >
                          <mat-label>{{
                            "digitalCards.profileLanguage" | translate
                          }}</mat-label>
                          <mat-select formControlName="profileLanguage">
                            <mat-option value="">{{
                              "digitalCards.profileLanguageDefault" | translate
                            }}</mat-option>
                            @for (
                              language of enabledProfileLanguages;
                              track language
                            ) {
                              <mat-option [value]="language">{{
                                profileLanguageLabelKeys[language] | translate
                              }}</mat-option>
                            }
                          </mat-select>
                        </mat-form-field>
                      </div>
                    </section>
                  }
                  <section class="dc-block">
                    <header class="dc-block__head">
                      <h3 class="dc-block__title">
                        {{ "digitalCards.sectionBranding" | translate }}
                      </h3>
                    </header>
                    <div class="dc-block__body">
                      <div class="dc-color-grid">
                        <app-print-color
                          [label]="
                            'digitalCard.settings.brandColorPrimary' | translate
                          "
                          [value]="
                            form.get('brandColorPrimary')?.value || '#000000'
                          "
                          [showCmyk]="false"
                          (valueChange)="
                            onFormColorChange('brandColorPrimary', $event)
                          "
                        ></app-print-color>
                        <app-print-color
                          [label]="
                            'digitalCard.settings.cardBackgroundColor'
                              | translate
                          "
                          [value]="
                            form.get('cardBackgroundColor')?.value || '#ffffff'
                          "
                          [showCmyk]="false"
                          (valueChange)="
                            onFormColorChange('cardBackgroundColor', $event)
                          "
                        ></app-print-color>
                        <app-print-color
                          [label]="
                            'digitalCard.settings.pageBackgroundColor'
                              | translate
                          "
                          [value]="
                            form.get('pageBackgroundColor')?.value || '#ffffff'
                          "
                          [showCmyk]="false"
                          (valueChange)="
                            onFormColorChange('pageBackgroundColor', $event)
                          "
                        ></app-print-color>
                      </div>
                    </div>
                  </section>

                  <section class="dc-block">
                    <header class="dc-block__head">
                      <h3 class="dc-block__title">
                        {{ "digitalCards.fontSectionTitle" | translate }}
                      </h3>
                    </header>
                    <div class="dc-block__body">
                      <mat-form-field
                        appearance="outline"
                        class="dc-form-field-full"
                      >
                        <mat-label>{{
                          "digitalCards.fontLabel" | translate
                        }}</mat-label>
                        <mat-select formControlName="fontPreset">
                          @for (font of fontPresets; track font.id) {
                            <mat-option [value]="font.id">
                              {{ font.label }}
                            </mat-option>
                          }
                        </mat-select>
                      </mat-form-field>
                      @if (!companyBrandFontReady) {
                        <p class="dc-font-upload-hint">
                          {{ "digitalCards.companyFontMissing" | translate
                          }}<a routerLink="/settings/company">{{
                            "digitalCards.companyFontSettingsLink" | translate
                          }}</a>
                        </p>
                      }
                    </div>
                  </section>

                  <section class="dc-block">
                    <header class="dc-block__head">
                      <h3 class="dc-block__title">
                        {{ "digitalCards.sectionBanner" | translate }}
                      </h3>
                    </header>
                    <div class="dc-block__body">
                      <div
                        class="dc-toggle-list dc-toggle-list--stacked"
                        [class.dc-toggle-list--joins-next]="
                          bannerOptionsVisible()
                        "
                      >
                        <div class="dc-toggle-item">
                          <mat-slide-toggle
                            color="primary"
                            formControlName="showBanner"
                            class="dc-toggle dc-toggle-item__control"
                          >
                            {{ "digitalCard.settings.showBanner" | translate }}
                          </mat-slide-toggle>
                        </div>
                      </div>
                      @if (bannerOptionsVisible()) {
                        <div class="dc-revealed-panel">
                          <mat-form-field
                            appearance="outline"
                            class="dc-form-field-full"
                          >
                            <mat-label>{{
                              "digitalCard.settings.bannerType" | translate
                            }}</mat-label>
                            <mat-select formControlName="bannerType">
                              <mat-option value="gradient">{{
                                "digitalCard.settings.bannerGradient"
                                  | translate
                              }}</mat-option>
                              <mat-option value="image">{{
                                "digitalCard.settings.bannerImage" | translate
                              }}</mat-option>
                            </mat-select>
                          </mat-form-field>
                          @if (form.get("bannerType")?.value === "gradient") {
                            <div class="dc-color-grid">
                              <app-print-color
                                [label]="
                                  'digitalCard.settings.gradientStart'
                                    | translate
                                "
                                [value]="
                                  form.get('bannerGradientStart')?.value ||
                                  '#ffffff'
                                "
                                [showCmyk]="false"
                                (valueChange)="
                                  onFormColorChange(
                                    'bannerGradientStart',
                                    $event
                                  )
                                "
                              ></app-print-color>
                              <app-print-color
                                [label]="
                                  'digitalCard.settings.gradientEnd' | translate
                                "
                                [value]="
                                  form.get('bannerGradientEnd')?.value ||
                                  '#f5f5f0'
                                "
                                [showCmyk]="false"
                                (valueChange)="
                                  onFormColorChange('bannerGradientEnd', $event)
                                "
                              ></app-print-color>
                            </div>
                          }
                          @if (form.get("bannerType")?.value === "image") {
                            <div class="dc-upload">
                              <span class="dc-upload__label">{{
                                "digitalCard.settings.bannerImageUpload"
                                  | translate
                              }}</span>
                              <app-image-uploader
                                [allowedFileExtensions]="rasterMimeTypes"
                                [previewUrl]="bannerPreviewUrl"
                                [uploadFn]="uploadBanner"
                                (onFileChange)="onBannerFileChange($event)"
                                (onFileRemove)="removeBanner()"
                              ></app-image-uploader>
                            </div>
                            @if (showFreezeFrame) {
                              <div class="dc-gif-freeze">
                                <div class="dc-gif-freeze__label">
                                  {{
                                    "digitalCard.settings.bannerFreezeFrame"
                                      | translate
                                  }}
                                </div>
                                <div
                                  class="dc-gif-freeze__preview"
                                  [class.dc-gif-freeze__preview--loading]="
                                    freezeFrameLoading
                                  "
                                >
                                  @if (freezeFrameLoading) {
                                    <div class="dc-gif-freeze__loader"></div>
                                  } @else if (freezeFramePreviewUrl) {
                                    @for (
                                      url of [freezeFramePreviewUrl];
                                      track freezeFrameIndex
                                    ) {
                                      <img [src]="url" alt="" />
                                    }
                                  } @else if (bannerPosterPreviewUrl) {
                                    <img [src]="bannerPosterPreviewUrl" alt="" />
                                  }
                                </div>
                                @if (showFreezeFrameSlider) {
                                  <div class="dc-gif-freeze__controls">
                                    <input
                                      type="range"
                                      class="dc-gif-freeze__slider"
                                      min="0"
                                      [max]="freezeFrameCount - 1"
                                      step="1"
                                      [value]="freezeFrameIndex"
                                      (input)="
                                        onFreezeFrameIndexInput(
                                          $any($event.target).value
                                        )
                                      "
                                    />
                                    <span class="dc-gif-freeze__meta">
                                      {{ freezeFrameIndex + 1 }} /
                                      {{ freezeFrameCount }}
                                    </span>
                                  </div>
                                }
                              </div>
                            }
                          }
                        </div>
                      }
                    </div>
                  </section>

                  <section class="dc-block">
                    <header class="dc-block__head">
                      <h3 class="dc-block__title">
                        {{ "digitalCards.sectionLogo" | translate }}
                      </h3>
                    </header>
                    <div class="dc-block__body">
                      <div
                        class="dc-toggle-list dc-toggle-list--stacked"
                        [class.dc-toggle-list--joins-next]="
                          companyLogoOptionsVisible()
                        "
                      >
                        <div class="dc-toggle-item">
                          <mat-slide-toggle
                            color="primary"
                            formControlName="showCompanyLogo"
                            class="dc-toggle dc-toggle-item__control"
                          >
                            {{ "digitalCards.showCompanyLogo" | translate }}
                          </mat-slide-toggle>
                        </div>
                      </div>
                      @if (companyLogoOptionsVisible()) {
                        <div class="dc-revealed-panel">
                          <app-searchable-select
                            class="dc-form-field-full"
                            formControlName="companyLogoUrl"
                            [label]="'digitalCards.companyLogoSelectLabel' | translate"
                            [options]="companyLogoSelectOptions()"
                            [displayWith]="mediaLabel"
                            [valueWith]="mediaValue"
                            [imageWith]="mediaImage"
                            [emptyLabel]="'digitalCards.companyLogoOrganizationDefault' | translate"
                            [emptyValue]="''"
                            [loading]="mediaImagesLoading"
                            [disabled]="mediaImagesLoading"
                          ></app-searchable-select>
                          @if (!mediaImagesLoading && mediaImages.length === 0) {
                            <mat-hint>{{
                              "digitalCards.companyLogoMediaEmptyHint"
                                | translate
                            }}</mat-hint>
                          }
                        </div>
                      }
                    </div>
                  </section>

                  <section class="dc-block">
                    <header class="dc-block__head">
                      <h3 class="dc-block__title">
                        {{ "digitalCards.sectionLayout" | translate }}
                      </h3>
                    </header>
                    <div class="dc-block__body">
                      <div class="dc-toggle-list dc-toggle-list--stacked">
                        <div class="dc-toggle-item">
                          <mat-slide-toggle
                            color="primary"
                            formControlName="showAvatar"
                            class="dc-toggle dc-toggle-item__control"
                          >
                            {{ "digitalCard.settings.showAvatar" | translate }}
                          </mat-slide-toggle>
                        </div>
                        <div class="dc-toggle-item">
                          <mat-slide-toggle
                            color="primary"
                            formControlName="showJobTitle"
                            class="dc-toggle dc-toggle-item__control"
                          >
                            {{
                              "digitalCard.settings.showJobTitle" | translate
                            }}
                          </mat-slide-toggle>
                        </div>
                        <div class="dc-toggle-item">
                          <mat-slide-toggle
                            color="primary"
                            formControlName="showAboutMe"
                            class="dc-toggle dc-toggle-item__control"
                          >
                            {{ "digitalCard.settings.showAboutMe" | translate }}
                          </mat-slide-toggle>
                        </div>
                        <div class="dc-toggle-item" *ngIf="namePronunciationEnabled">
                          <mat-slide-toggle
                            color="primary"
                            formControlName="showPronounceName"
                            class="dc-toggle dc-toggle-item__control"
                          >
                            {{
                              "digitalCard.settings.showPronounceName"
                                | translate
                            }}
                          </mat-slide-toggle>
                        </div>
                        <div class="dc-toggle-item">
                          <mat-slide-toggle
                            color="primary"
                            formControlName="showSaveContact"
                            class="dc-toggle dc-toggle-item__control"
                          >
                            {{
                              "digitalCard.settings.showSaveContact" | translate
                            }}
                          </mat-slide-toggle>
                        </div>
                        <div class="dc-toggle-item">
                          <mat-slide-toggle
                            color="primary"
                            formControlName="showBookMeeting"
                            class="dc-toggle dc-toggle-item__control"
                          >
                            {{
                              "digitalCard.settings.showBookMeeting" | translate
                            }}
                          </mat-slide-toggle>
                        </div>
                      </div>
                    </div>
                  </section>

                  <section class="dc-block">
                    <header class="dc-block__head">
                      <h3 class="dc-block__title">
                        {{ "digitalCards.sectionFields" | translate }}
                      </h3>
                    </header>
                    <div class="dc-block__body">
                      <div
                        cdkDropList
                        [cdkDropListData]="orderedFieldToggles"
                        (cdkDropListDropped)="dropContactField($event)"
                        class="dc-field-order-list"
                      >
                        @for (toggle of orderedFieldToggles; track toggle.key) {
                          <div
                            class="dc-field-order-item"
                            cdkDrag
                            [cdkDragData]="toggle"
                          >
                            <button
                              mat-icon-button
                              type="button"
                              cdkDragHandle
                              class="dc-field-order-item__handle"
                              [attr.aria-label]="toggle.label"
                            >
                              <mat-icon>drag_indicator</mat-icon>
                            </button>
                            <mat-slide-toggle
                              color="primary"
                              class="dc-toggle dc-field-order-item__toggle"
                              [checked]="isFieldVisible(toggle.key)"
                              (change)="
                                toggleFieldVisibility(
                                  toggle.key,
                                  $event.checked
                                )
                              "
                              >{{ toggle.label }}</mat-slide-toggle
                            >
                          </div>
                        }
                      </div>
                    </div>
                  </section>
                </div>
              </mat-tab>
              <mat-tab *ngIf="walletTabEnabled">
                <ng-template mat-tab-label>
                  <mat-icon class="dc-tab-icon"
                    >account_balance_wallet</mat-icon
                  >
                  {{ "digitalCards.tabWallet" | translate }}
                </ng-template>
                <div class="dc-tab-body" formGroupName="walletDraft">
                  <section class="dc-block">
                    <header class="dc-block__head">
                      <h3 class="dc-block__title">
                        {{
                          "digitalCards.walletCustomBackgroundLabel" | translate
                        }}
                      </h3>
                    </header>
                    <div class="dc-block__body">
                      <div
                        class="dc-toggle-list dc-toggle-list--stacked"
                        [class.dc-toggle-list--joins-next]="
                          walletBackgroundOptionsVisible()
                        "
                      >
                        <div class="dc-toggle-item">
                          <mat-slide-toggle
                            color="primary"
                            formControlName="inheritWalletBackgroundFromCard"
                            class="dc-toggle dc-toggle-item__control"
                            (change)="
                              onWalletInheritBackgroundToggle($event.checked)
                            "
                          >
                            {{
                              "digitalCards.walletInheritBrandColor" | translate
                            }}
                          </mat-slide-toggle>
                        </div>
                      </div>
                      @if (walletBackgroundOptionsVisible()) {
                        <div class="dc-revealed-panel">
                          <div class="dc-color-grid">
                            <app-print-color
                              [label]="
                                'digitalCards.walletCustomBackgroundLabel'
                                  | translate
                              "
                              [value]="walletUnifiedBgPickerValue()"
                              [showCmyk]="false"
                              (valueChange)="onWalletUnifiedBgChange($event)"
                            ></app-print-color>
                          </div>
                        </div>
                      }
                    </div>
                  </section>

                  <section class="dc-block">
                    <header class="dc-block__head">
                      <h3 class="dc-block__title">
                        {{ "digitalCards.walletSectionLogo" | translate }}
                      </h3>
                    </header>
                    <div class="dc-block__body">
                      <div
                        class="dc-toggle-list dc-toggle-list--stacked"
                        [class.dc-toggle-list--joins-next]="
                          walletPassLogoEnabled()
                        "
                      >
                        <div class="dc-toggle-item">
                          <mat-slide-toggle
                            color="primary"
                            class="dc-toggle dc-toggle-item__control"
                            [checked]="walletPassLogoEnabled()"
                            (change)="onWalletPassLogoToggle($event.checked)"
                          >
                            {{ "digitalCards.walletShowPassLogo" | translate }}
                          </mat-slide-toggle>
                        </div>
                        <div class="dc-toggle-item">
                          <mat-slide-toggle
                            color="primary"
                            class="dc-toggle dc-toggle-item__control"
                            formControlName="walletShowPassCompanyName"
                          >
                            {{
                              "digitalCards.walletShowPassCompanyName"
                                | translate
                            }}
                          </mat-slide-toggle>
                        </div>
                      </div>
                      @if (walletPassLogoEnabled()) {
                        <div class="dc-revealed-panel">
                          <app-searchable-select
                            class="dc-form-field-full"
                            [label]="'digitalCards.companyLogoSelectLabel' | translate"
                            [value]="walletLogoUnifiedValue()"
                            (valueChange)="onWalletLogoUnifiedChange($event)"
                            [options]="walletLogoSelectOptions()"
                            [displayWith]="mediaLabel"
                            [valueWith]="mediaValue"
                            [imageWith]="mediaImage"
                            [emptyLabel]="'digitalCards.walletLogoInherit' | translate"
                            [emptyValue]="walletLogoSelInherit"
                            [loading]="mediaImagesLoading"
                            [disabled]="mediaImagesLoading"
                            subscriptSizing="dynamic"
                          ></app-searchable-select>
                          @if (!mediaImagesLoading && mediaImages.length === 0) {
                            <mat-hint>{{
                              "digitalCards.companyLogoMediaEmptyHint"
                                | translate
                            }}</mat-hint>
                          }
                        </div>
                      }
                    </div>
                  </section>

                  <section class="dc-block">
                    <header class="dc-block__head">
                      <h3 class="dc-block__title">
                        {{ "digitalCards.walletSectionData" | translate }}
                      </h3>
                    </header>
                    <div class="dc-block__body">
                      <div
                        class="dc-toggle-list dc-toggle-list--stacked"
                        [class.dc-toggle-list--joins-next]="
                          walletFieldOverridesVisible()
                        "
                      >
                        <div class="dc-toggle-item">
                          <mat-slide-toggle
                            color="primary"
                            formControlName="inheritCardFieldVisibility"
                            class="dc-toggle dc-toggle-item__control"
                          >
                            {{
                              "digitalCards.walletInheritFromCard" | translate
                            }}
                          </mat-slide-toggle>
                        </div>
                      </div>
                      @if (walletFieldOverridesVisible()) {
                        <div class="dc-toggle-list dc-toggle-list--stacked">
                          <div class="dc-toggle-item">
                            <mat-slide-toggle
                              color="primary"
                              formControlName="walletShowAvatar"
                              class="dc-toggle dc-toggle-item__control"
                            >
                              {{ "digitalCards.walletShowAvatar" | translate }}
                            </mat-slide-toggle>
                          </div>
                          <div class="dc-toggle-item">
                            <mat-slide-toggle
                              color="primary"
                              formControlName="walletShowJobTitle"
                              class="dc-toggle dc-toggle-item__control"
                            >
                              {{
                                "digitalCards.walletShowJobTitle" | translate
                              }}
                            </mat-slide-toggle>
                          </div>
                          <div class="dc-toggle-item">
                            <mat-slide-toggle
                              color="primary"
                              formControlName="walletShowEmail"
                              class="dc-toggle dc-toggle-item__control"
                            >
                              {{
                                "digitalCards.walletContactEmail" | translate
                              }}
                            </mat-slide-toggle>
                          </div>
                          <div class="dc-toggle-item">
                            <mat-slide-toggle
                              color="primary"
                              formControlName="walletShowPhone"
                              class="dc-toggle dc-toggle-item__control"
                            >
                              {{
                                "digitalCards.walletContactPhone" | translate
                              }}
                            </mat-slide-toggle>
                          </div>
                        </div>
                      }
                    </div>
                  </section>

                  <section class="dc-block">
                    <header class="dc-block__head">
                      <h3 class="dc-block__title">
                        {{ "digitalCards.walletQrCaptionLabel" | translate }}
                      </h3>
                    </header>
                    <div class="dc-block__body">
                      <mat-form-field
                        appearance="outline"
                        class="dc-form-field-full"
                      >
                        <mat-label>{{
                          "digitalCards.walletQrCaptionLabel" | translate
                        }}</mat-label>
                        <input matInput formControlName="walletQrCaption" />
                      </mat-form-field>
                    </div>
                  </section>
                </div>
              </mat-tab>
              <mat-tab>
                <ng-template mat-tab-label>
                  <mat-icon class="dc-tab-icon">code</mat-icon>
                  {{ "digitalCards.tabCustomHtml" | translate }}
                </ng-template>
                <div class="dc-tab-body">
                  <section class="dc-block">
                    <header class="dc-block__head">
                      <h3 class="dc-block__title">
                        {{ "digitalCards.customHtml" | translate }}
                      </h3>
                    </header>
                    <div class="dc-block__body">
                      <div class="dc-custom-html-editor-wrap">
                        <app-editor
                          #customHtmlEditor
                          formControlName="customHtml"
                          [height]="400"
                        ></app-editor>
                      </div>
                      <app-variable-palette
                        [targetEditor]="customHtmlEditor"
                        [isExternalExpanded]="true"
                      ></app-variable-palette>
                    </div>
                  </section>
                </div>
              </mat-tab>
            </mat-tab-group>
          </div>
        </div>
      </div>
    </div>
  </form>
  @if (!pageReady) {
    <div class="dbc1-loading dbc1-loading--overlay">
      <app-loading></app-loading>
    </div>
  }
</div>
